import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { INPUT_CLS, SELECT_CONTENT_CLS, SELECT_ITEM_CLS, LABEL_CLS, SectionHeader } from '@/components/brand/BrandKit';

const VEHICLE_CLASSES = [
  { value: "economy", label: "Economy (Corolla / Elantra)", monthly: 1750 },
  { value: "midsuv", label: "Mid-size SUV (Tucson / CR-V)", monthly: 2400 },
  { value: "premium", label: "Premium Sedan (Camry / Accord)", monthly: 3060 },
  { value: "fullsuv", label: "Full-size SUV (Patrol / Land Cruiser)", monthly: 4500 },
  { value: "luxury", label: "Luxury (Range Rover / Cadillac)", monthly: 7200 },
];
const VEHICLE_CLASSES_AR = [
  { value: "economy", label: "اقتصادي (كورولا / إلنترا)", monthly: 1750 },
  { value: "midsuv", label: "SUV متوسط (توسان / CR-V)", monthly: 2400 },
  { value: "premium", label: "سيدان متميز (كامري / أكورد)", monthly: 3060 },
  { value: "fullsuv", label: "SUV كامل (باترول / لاند كروزر)", monthly: 4500 },
  { value: "luxury", label: "فاخر (رينج روفر / كاديلاك)", monthly: 7200 },
];

const T = {
  en: { chip: 'Fleet Savings Calculator', title: 'See what operational leasing saves your fleet.', desc: 'Adjust fleet size, term and vehicle class to compare against buying outright. Figures are indicative UAE market averages.', fleet: 'Fleet size', vehicles: 'vehicles', term: 'Term', months: 'months', cls: 'Vehicle class', est: (m) => `Estimated ${m}-month saving`, vs: 'saving vs buying', buy: 'Buy outright', buyNote: 'Depreciation + insurance + maintenance + financing', lease: 'Eurogulf leasing', perMonth: '/month · all inclusive', cta: 'Customise Your Lease', ctaHref: '/contact', note: 'Speak to our fleet specialists for a tailored savings proposal.', classes: VEHICLE_CLASSES },
  ar: { chip: 'حاسبة وفورات الأسطول', title: 'اكتشف ما يوفّره التأجير التشغيلي لأسطولك.', desc: 'عدّل حجم الأسطول والمدة وفئة المركبة للمقارنة مع الشراء المباشر. الأرقام تقديرية وفق متوسطات السوق الإماراتي.', fleet: 'حجم الأسطول', vehicles: 'مركبة', term: 'المدة', months: 'شهراً', cls: 'فئة المركبة', est: (m) => `الوفورات التقديرية خلال ${m} شهراً`, vs: 'وفورات مقارنة بالشراء', buy: 'الشراء المباشر', buyNote: 'الاستهلاك + التأمين + الصيانة + التمويل', lease: 'التأجير من يوروجلف', perMonth: '/شهرياً · شامل كل شيء', cta: 'صمّم عقد التأجير الخاص بك', ctaHref: '/ar/contact', note: 'تحدث إلى مختصي الأساطيل لدينا للحصول على عرض وفورات مخصص.', classes: VEHICLE_CLASSES_AR },
};

function termDiscount(m) {
  if (m >= 48) return 0.15;
  if (m >= 36) return 0.1;
  if (m >= 24) return 0.05;
  return 0;
}
function volumeDiscount(n) {
  if (n >= 10) return 0.15;
  if (n >= 5) return 0.08;
  return 0;
}
function buyMultiplier(m) {
  if (m >= 36) return 1.35;
  if (m >= 24) return 1.28;
  return 1.2;
}

export function computeSavings({ fleetSize, term, vehicleClass }) {
  const vehicle = VEHICLE_CLASSES.find((v) => v.value === vehicleClass);
  const months = parseInt(term, 10);
  const discount = Math.min(termDiscount(months) + volumeDiscount(fleetSize), 0.25);
  const leaseMonthly = Math.round(vehicle.monthly * (1 - discount));
  const leasingTotal = leaseMonthly * months * fleetSize;
  const buyTotal = Math.round(vehicle.monthly * buyMultiplier(months) * months * fleetSize);
  const saving = buyTotal - leasingTotal;
  return { leaseMonthly, leasingTotal, buyTotal, saving, savingPct: buyTotal > 0 ? Math.round((saving / buyTotal) * 100) : 0 };
}

export function FleetCalculator({ lang = 'en' }) {
  const t = T[lang] || T.en;
  const [fleetSize, setFleetSize] = useState(10);
  const [term, setTerm] = useState("36");
  const [vehicleClass, setVehicleClass] = useState("premium");
  const r = computeSavings({ fleetSize, term, vehicleClass });

  return (
    <section data-testid="fleet-calculator" className="bg-[#F5F5F3] py-14 sm:py-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader compact chip={t.chip} title={t.title} align="center" desc={t.desc} />
        <div className="mt-10 rounded-2xl border border-black/[0.06] bg-white p-6 sm:p-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div>
              <label htmlFor="fleet-size" className={LABEL_CLS}>{t.fleet}</label>
              <input id="fleet-size" data-testid="calc-fleet-size" type="range" min="1" max="100" value={fleetSize} onChange={(e) => setFleetSize(parseInt(e.target.value, 10))} className="w-full accent-[#EE5A01]" />
              <p className="mt-1 font-heading font-bold text-sm text-[#EE5A01]">{fleetSize} {t.vehicles}</p>
            </div>
            <div>
              <span className={LABEL_CLS}>{t.term}</span>
              <Select value={term} onValueChange={setTerm}>
                <SelectTrigger data-testid="calc-term" className={INPUT_CLS}><SelectValue /></SelectTrigger>
                <SelectContent className={SELECT_CONTENT_CLS}>{["12", "24", "36", "48", "60"].map((m) => <SelectItem key={m} value={m} className={SELECT_ITEM_CLS}>{m} {t.months}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <span className={LABEL_CLS}>{t.cls}</span>
              <Select value={vehicleClass} onValueChange={setVehicleClass}>
                <SelectTrigger data-testid="calc-vehicle-class" className={INPUT_CLS}><SelectValue /></SelectTrigger>
                <SelectContent className={SELECT_CONTENT_CLS}>{t.classes.map((v) => <SelectItem key={v.value} value={v.value} className={SELECT_ITEM_CLS}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-8 text-center">
            <p className="font-heading font-bold text-[11px] tracking-[0.15em] uppercase text-[#666666]">{t.est(term)}</p>
            <p data-testid="calc-saving" className="mt-1 font-heading font-black text-4xl sm:text-5xl text-[#EE5A01] tracking-tight" dir="ltr">AED {r.saving.toLocaleString()}</p>
            <p className="font-body text-sm text-[#666666]">{r.savingPct}% {t.vs}</p>
          </div>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-xl bg-[#F5F5F3] p-5 text-center"><p className="font-heading font-bold text-[11px] uppercase tracking-wider text-[#666666]">{t.buy}</p><p className="mt-1 font-heading font-bold text-2xl text-[#121212]" dir="ltr">AED {r.buyTotal.toLocaleString()}</p><p className="mt-1 font-body text-[11px] text-[#666666]">{t.buyNote}</p></div>
            <div className="rounded-xl border border-[#EE5A01]/30 bg-[#FFF4ED] p-5 text-center"><p className="font-heading font-bold text-[11px] uppercase tracking-wider text-[#EE5A01]">{t.lease}</p><p className="mt-1 font-heading font-bold text-2xl text-[#EE5A01]" dir="ltr">AED {r.leasingTotal.toLocaleString()}</p><p className="mt-1 font-body text-[11px] text-[#666666]"><span dir="ltr">AED {r.leaseMonthly.toLocaleString()}</span>{t.perMonth}</p></div>
          </div>
        </div>
        <div className="mt-8 text-center">
          <Link to={t.ctaHref} data-testid="fleet-calculator-cta" className="btn-primary">{t.cta}</Link>
          <p className="mt-3 font-body text-xs text-[#666666]">{t.note}</p>
        </div>
      </div>
    </section>
  );
}
